import Close from "@/assets/icons/Close";
import { HStack } from "@/components/ui/hstack";
import { Toast, ToastDescription, ToastTitle, useToast } from "@/components/ui/toast";
import { VStack } from "@/components/ui/vstack";
import { useToastNotify } from "@/stores/toastStore";
import { capitalizeWords } from "@/utils/capitalizeWords";
import { T } from "@transifex/react";
import { useEffect, useRef, useState } from "react";
import { Animated, Dimensions, PanResponder, type PanResponderInstance, Pressable } from "react-native";

interface ToastItemProps {
  toastItem: {
    id: string;
    severity: "success" | "error" | "warning" | "info";
    message: string;
    duration?: number;
  };
  maxToastWidth: number;
  onCloseComplete: () => void;
}

const ToastItem = ({ toastItem, maxToastWidth, onCloseComplete }: ToastItemProps) => {
  const toast = useToast();
  const { id, severity, message, duration } = toastItem;
  const [translateY] = useState(new Animated.Value(0));
  const panResponderRef = useRef<PanResponderInstance | null>(null);

  useEffect(() => {
    if (!toast.isActive(id)) {
      panResponderRef.current = PanResponder.create({
        onMoveShouldSetPanResponder: (_, gestureState) => gestureState.dy > 10,
        onPanResponderMove: (_, gestureState) => {
          if (gestureState.dy > 0) {
            translateY.setValue(gestureState.dy);
          }
        },
        onPanResponderRelease: (_, gestureState) => {
          if (gestureState.dy > 100) {
            Animated.timing(translateY, {
              toValue: 300,
              duration: 200,
              useNativeDriver: true,
            }).start(handleClose);
          } else {
            Animated.spring(translateY, {
              toValue: 0,
              useNativeDriver: true,
            }).start();
          }
        },
      });

      const handleClose = () => {
        toast.close(id);
        onCloseComplete();
      };

      toast.show({
        id,
        placement: "bottom",
        duration: duration ?? 5000,
        render: ({ id }) => {
          const uniqueToastId = `toast-${id}`;
          return (
            <Animated.View
              {...(panResponderRef.current ? panResponderRef.current.panHandlers : {})}
              style={{
                transform: [{ translateY }],
              }}
            >
              <Toast
                action={severity}
                variant="outline"
                nativeID={uniqueToastId}
                style={{ width: maxToastWidth, alignSelf: "center", bottom: 20 }}
                className={`bg-${severity}-50 border border-${severity}-300 mt-[17px] p-[16px] rounded-[6px] shadow-[0_16px_20px_rgba(0,0,0,.1)]`}
              >
                <VStack className="gap-[8px]">
                  <HStack>
                    <ToastTitle className={`font-semibold text-${severity}-500 flex-1`}>
                      <T _str={capitalizeWords(severity)} />
                    </ToastTitle>
                    <Pressable onPress={handleClose}>
                      <Close height={24} width={24} />
                    </Pressable>
                  </HStack>
                  <ToastDescription>{message}</ToastDescription>
                </VStack>
              </Toast>
            </Animated.View>
          );
        },
        onCloseComplete,
      });
    }
  }, [id, severity, message, duration, toast, maxToastWidth, onCloseComplete, translateY]);

  return null;
};

export const ToastContainer = () => {
  const { toasts, removeToast } = useToastNotify();
  const { width } = Dimensions.get("window");
  const maxToastWidth = Math.min(width * 0.8, 350);

  return (
    <>
      {toasts.map((toastItem) => (
        <ToastItem key={toastItem.id} toastItem={toastItem} maxToastWidth={maxToastWidth} onCloseComplete={() => removeToast(toastItem.id)} />
      ))}
    </>
  );
};
